import useQueryParameters from 'hooks/useQueryParameters';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectPrequalOffer } from 'state/prequalOffer';
import useFeature from 'hooks/useFeature';

import { selectExpandedPanel } from 'state/unifiedWidgets';

import { Step, Workflow } from './Workflow';

export const useUnifiedIsInSession = () => {
  const {
    financingBuyerId,
    prequalificationId,
    hasExistingAccount,
  } = useQueryParameters();

  const unifiedIsInSession = useMemo(() => {
    return (
      financingBuyerId !== undefined ||
      prequalificationId !== undefined ||
      hasExistingAccount
    );
  }, [financingBuyerId, prequalificationId, hasExistingAccount]);
  return unifiedIsInSession;
};

const useUnifiedPrequalCheckoutWorkflow = () => {
  const { prequalificationId, hasOffer, notQualified } = useSelector(
    selectPrequalOffer,
  );

  const inSession = useUnifiedIsInSession();
  const { selectedCardKey } = useQueryParameters();
  const { isEnabled } = useFeature();
  const expandedPanel = useSelector(selectExpandedPanel);
  const getWorkflow = useCallback((): Workflow => {
    const cardSelectorCheck =
      isEnabled('cardSelector') &&
      !selectedCardKey &&
      // card selector is part of the workflow until bread option selected
      (expandedPanel !== 'splitpay' && expandedPanel !== 'installments');

    const applicationReviewCheck: boolean =
      !!notQualified || (!!prequalificationId && !!hasOffer);

    const stepsEnabled = {
      [Step.UNIFIED_CHECKOUT_HOME]: true,
      [Step.UNIFIED_OFFER_INTRO]: !inSession,
      [Step.CARD_SELECTOR]: cardSelectorCheck && !inSession,
      [Step.UNIFIED_CONSUMER_SEARCH]: !inSession,
      [Step.UNIFIED_OFFER_APPLICATION]: !inSession,
      [Step.UNIFIED_PREQUAL_CHECKOUT_OFFER]: true,
      [Step.SECOND_CARD_SELECTOR]: cardSelectorCheck,
      [Step.UNIFIED_PREQUAL_OFFER_PUSH_APPLICATION]: inSession,
      [Step.UNIFIED_APPLICATION_REVIEW]: applicationReviewCheck,
    };

    return {
      stepsEnabled: stepsEnabled,
      paths: {
        [Step.UNIFIED_CHECKOUT_HOME]: '/unified/checkout',
        [Step.UNIFIED_OFFER_INTRO]: '/unified/checkout-start',
        [Step.CARD_SELECTOR]: '/card-selector',
        [Step.UNIFIED_CONSUMER_SEARCH]: '/unified/consumer-search',
        [Step.UNIFIED_OFFER_APPLICATION]: '/unified/offer-application',
        [Step.UNIFIED_PREQUAL_CHECKOUT_OFFER]: '/unified/checkout-offer',
        [Step.SECOND_CARD_SELECTOR]: '/card-selector-fallback',
        [Step.UNIFIED_PREQUAL_OFFER_PUSH_APPLICATION]:
          '/unified/prequal-offer-push-application',
        [Step.UNIFIED_APPLICATION_REVIEW]: notQualified
          ? '/unified/credit-card-offer/credit-review'
          : '/unified/credit-card-offer/prequal-review',
      },
      progressStartStep: Step.UNIFIED_CONSUMER_SEARCH,
      restartStep: Step.UNIFIED_CHECKOUT_HOME,
      timeoutRestartPath: '/unified/checkout',
    };
  }, [
    expandedPanel,
    hasOffer,
    inSession,
    isEnabled,
    notQualified,
    prequalificationId,
    selectedCardKey,
  ]);

  return getWorkflow;
};

export default useUnifiedPrequalCheckoutWorkflow;
